import { useApiTradeV1TradeCompaniesRetrieve } from 'api';
import {
  Form,
  SiteMaterialAutocomplete,
  TradeCompanyAutocomplete,
  TradeCompanySiteSelector,
  useForm,
  useResetOnChange,
} from 'form';
import { Link } from 'react-router-dom';
import { Button, Stack } from 'ui';

type FormValues = {
  tradeCompany: number;
  site: number;
  material: number;
};

export const SiteForm = ({ onChange }: { onChange?: (siteId?: number) => void }) => {
  const form = useForm<FormValues>();

  const { watch, resetField } = form;

  const [selectedTradeCompany, selectedSite] = watch(['tradeCompany', 'site']);

  const { data: company, isLoading: isCompanyLoading } = useApiTradeV1TradeCompaniesRetrieve(
    selectedTradeCompany || 0,
    {
      query: {
        enabled: Boolean(selectedTradeCompany),
      },
    }
  );

  const disableSiteLink = !selectedTradeCompany;
  const disableMaterialLink = !selectedTradeCompany;

  useResetOnChange<FormValues>(selectedTradeCompany, ['material', 'site'], resetField);
  useResetOnChange<FormValues>(selectedSite, ['material'], resetField);

  return (
    <Form {...form}>
      <Stack direction="column" alignItems="flex-end" justifyContent="center">
        <Button
          variant="text"
          component={Link}
          target="_blank"
          to="/ground-control/user_onboarding/"
          aria-label="Add new company"
        >
          Add new company
        </Button>
        <TradeCompanyAutocomplete name="tradeCompany" label="Company" size="medium" required />
      </Stack>

      <Stack direction="column" alignItems="flex-end" justifyContent="center">
        <Button
          variant="text"
          component={Link}
          target="_blank"
          to={`/ground-control/manage_company/${company?.company_id}`}
          aria-label="Add new site"
          loading={isCompanyLoading}
          disabled={disableSiteLink}
          data-testid="add-new-site-link"
        >
          Add new site
        </Button>
        <TradeCompanySiteSelector
          name="site"
          label="Site"
          size="medium"
          tradeCompanyId={selectedTradeCompany}
          required
        />
      </Stack>

      <Stack direction="column" alignItems="flex-end" justifyContent="center">
        <Button
          variant="text"
          component={Link}
          target="_blank"
          to={`/ground-control/v2/manage-company/${selectedTradeCompany}/site-materials/new`}
          aria-label="Add new material"
          loading={isCompanyLoading}
          disabled={disableMaterialLink}
          data-testid="add-new-material-link"
        >
          Add new material
        </Button>
        <SiteMaterialAutocomplete
          tradeCompanyId={selectedTradeCompany}
          tradeSite={selectedSite}
          name="material"
          label="Material"
          size="medium"
          required
          onSelectOption={(value) => {
            onChange?.(value?.value ? Number(value.value) : undefined);
          }}
        />
      </Stack>
    </Form>
  );
};
